import React, { useRef, useState } from "react";
import "./contact.scss";
import { 
  IoLocationSharp, 
  IoMail, 
  IoCall, 
  IoLogoWhatsapp,
  IoTime,
  IoGlobe,
  IoCheckmarkCircle
} from "react-icons/io5";
import { 
  FaFacebookF, 
  FaTwitter, 
  FaInstagram, 
  FaLinkedinIn,
  FaPaperPlane,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaClock
} from "react-icons/fa";
import { FiSend, FiMapPin, FiPhone, FiMail, FiClock } from "react-icons/fi";
import emailjs from "@emailjs/browser";

const ContactUs = () => {
  const form = useRef();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const sendEmail = (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    setError("");

    emailjs
      .sendForm(
        "service_m9z8kfh",
        "template_m7xcgkb", 
        form.current,
        "s0Ia630UYSA29Zffn"
      )
      .then(
        (result) => {
          setSuccess(true);
          setLoading(false);
          form.current.reset();
          
          // Auto-hide success message after 5 seconds
          setTimeout(() => {
            setSuccess(false);
          }, 5000);
        },
        (error) => {
          setError("Failed to send message. Please try again or contact us directly.");
          setLoading(false);
        }
      );
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="hero-content">
          <h1 className="hero-title">Get In Touch</h1>
          <p className="hero-subtitle">
            We're here to help you plan your perfect adventure. Reach out to our travel experts for personalized assistance.
          </p>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">24/7</span>
              <span className="stat-label">Support</span>
            </div>
            <div className="stat">
              <span className="stat-number">&lt; 2hr</span>
              <span className="stat-label">Response Time</span>
            </div>
            <div className="stat">
              <span className="stat-number">100%</span>
              <span className="stat-label">Satisfaction</span>
            </div>
          </div>
        </div>
      </section>

      <div className="contact-container">
        {/* Contact Information */}
        <div className="contact-info-section">
          <div className="info-card">
            <div className="info-header">
              <div className="info-icon">
                <FiMapPin />
              </div>
              <h3>Our Location</h3>
            </div>
            <div className="info-content">
              <p>Skardu, Gilgit-Baltistan, Pakistan</p>
              <a 
                href="https://maps.google.com/?q=Skardu+Gilgit-Baltistan+Pakistan" 
                target="_blank" 
                rel="noopener noreferrer"
                className="map-link"
              >
                <FaMapMarkerAlt /> View on Google Maps
              </a>
            </div>
          </div>

          <div className="info-card">
            <div className="info-header">
              <div className="info-icon">
                <FiPhone />
              </div>
              <h3>Contact Numbers</h3>
            </div>
            <div className="info-content">
              <div className="contact-method">
                <div className="method-icon">
                  <IoLogoWhatsapp />
                </div>
                <div>
                  <span className="method-label">WhatsApp</span>
                  <a href="https://wa.me/971555936448" className="method-value">
                    +971 55 593 6448
                  </a>
                </div>
              </div>
              
              <div className="contact-method">
                <div className="method-icon">
                  <FaPhoneAlt />
                </div>
                <div>
                  <span className="method-label">Phone</span>
                  <a href="tel:+923491589717" className="method-value">
                    +923 49 158 9717
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="info-card">
            <div className="info-header">
              <div className="info-icon">
                <FiMail />
              </div>
              <h3>Email Address</h3>
            </div>
            <div className="info-content">
              <div className="contact-method">
                <div className="method-icon">
                  <FaEnvelope />
                </div>
                <div>
                  <span className="method-label">General Inquiries</span>
                  <a href="mailto:worldadveturetours@gmail.com" className="method-value">
                    worldadveturetours@gmail.com
                  </a>
                </div>
              </div>
              
              <div className="contact-method">
                <div className="method-icon">
                  <IoMail />
                </div>
                <div>
                  <span className="method-label">Bookings</span>
                  <a href="mailto:info@worldadveturestours.com" className="method-value">
                    info@worldadveturestours.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="info-card">
            <div className="info-header">
              <div className="info-icon">
                <FiClock />
              </div>
              <h3>Business Hours</h3>
            </div>
            <div className="info-content">
              <div className="business-hours">
                <div className="time-slot">
                  <span className="day">Monday - Friday</span>
                  <span className="time">9:00 AM - 6:00 PM</span>
                </div>
                <div className="time-slot">
                  <span className="day">Saturday</span>
                  <span className="time">10:00 AM - 4:00 PM</span>
                </div>
                <div className="time-slot">
                  <span className="day">Sunday</span>
                  <span className="time">Emergency Only</span>
                </div>
                <div className="emergency-note">
                  <FaClock /> 24/7 Emergency Support Available
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form Section */}
        <div className="contact-form-section">
          <div className="form-card">
            <div className="form-header">
              <h2>Send us a Message</h2>
              <p>Fill out the form below and we'll get back to you within 2 hours</p>
            </div>

            <form ref={form} onSubmit={sendEmail} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">
                  <FiMapPin /> Your Full Name *
                </label>
                <input 
                  type="text" 
                  id="name"
                  name="user_name" 
                  placeholder="John Doe" 
                  required 
                  disabled={loading}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">
                    <FiMail /> Email Address *
                  </label>
                  <input 
                    type="email" 
                    id="email"
                    name="user_email" 
                    placeholder="john@example.com" 
                    required 
                    disabled={loading}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">
                    <FiPhone /> Phone Number
                  </label>
                  <input 
                    type="tel" 
                    id="phone"
                    name="user_phone" 
                    placeholder="+1 (234) 567-8900" 
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject">
                  <IoMail /> Subject
                </label>
                <select 
                  id="subject" 
                  name="subject" 
                  disabled={loading}
                >
                  <option value="">Select a topic</option>
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="Booking Information">Booking Information</option>
                  <option value="Group Tour">Group Tour Inquiry</option>
                  <option value="Custom Trip">Custom Trip Request</option>
                  <option value="Feedback">Feedback & Suggestions</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">
                  <FiSend /> Your Message *
                </label>
                <textarea 
                  id="message"
                  name="message" 
                  placeholder="Tell us about your travel plans, group size, preferred dates, or any special requirements..." 
                  rows="5" 
                  required 
                  disabled={loading}
                ></textarea>
                <div className="char-counter">
                  <span id="char-count">0</span> / 500 characters
                </div>
              </div>

              {success && (
                <div className="success-message">
                  <IoCheckmarkCircle />
                  <div>
                    <strong>Message Sent Successfully!</strong>
                    <p>We'll get back to you within 2 hours. Thank you for contacting us.</p>
                  </div>
                </div>
              )}

              {error && (
                <div className="error-message">
                  <IoTime />
                  <div>
                    <strong>Oops! Something went wrong</strong>
                    <p>{error}</p>
                  </div>
                </div>
              )}

              <button 
                type="submit" 
                className="submit-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Sending Message...
                  </>
                ) : (
                  <>
                    <FaPaperPlane />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Social Media */}
          <div className="social-card">
            <h3>Connect With Us</h3>
            <p>Follow us on social media for travel inspiration, updates, and exclusive offers</p>
            
            <div className="social-icons">
              <a 
                href="https://www.facebook.com/WorldAdventurTours" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon facebook"
              >
                <FaFacebookF />
                <span>Facebook</span>
              </a>
              
              <a 
                href="https://www.instagram.com/worldadventurestours/?hl=en" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon instagram"
              >
                <FaInstagram />
                <span>Instagram</span>
              </a>
              
              <a 
                href="https://x.com/Mr_awaisqarni" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon twitter"
              >
                <FaTwitter />
                <span>Twitter</span>
              </a>
              
              <a 
                href="https://www.linkedin.com/in/maroofawaisqarni" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon linkedin"
              >
                <FaLinkedinIn />
                <span>LinkedIn</span>
              </a>
              
              <a 
                href="https://wa.me/971555936448" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon whatsapp"
              >
                <IoLogoWhatsapp />
                <span>WhatsApp</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <div className="map-section">
        <div className="map-header">
          <h2>Find Us</h2>
          <p>Visit our office in beautiful Skardu, Gilgit-Baltistan</p>
        </div>
        <div className="map-container">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3321.0340367569925!2d75.45034541520411!3d35.297858480781075!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38e4b4d8a2b8b2b9%3A0x8f8f9b0b9b0b9b0b!2sSkardu%2C%20Gilgit-Baltistan%2C%20Pakistan!5e0!3m2!1sen!2s!4v1629999999999!5m2!1sen!2s"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            title="World Adventure Tours Location"
          ></iframe>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>What is your response time?</h4>
            <p>We typically respond within 2 hours during business hours. For urgent matters, call our emergency line.</p>
          </div>
          <div className="faq-item">
            <h4>Do you offer group discounts?</h4>
            <p>Yes! We offer special rates for groups of 4 or more. Contact us for customized group packages.</p>
          </div>
          <div className="faq-item">
            <h4>What payment methods do you accept?</h4>
            <p>We accept bank transfers, credit cards, and mobile payment methods. 50% deposit required for booking.</p>
          </div>
          <div className="faq-item">
            <h4>Can I customize my tour?</h4>
            <p>Absolutely! We specialize in creating customized itineraries based on your preferences and interests.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;